import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CourseContext = createContext();

export const useCourse = () => {
  const context = useContext(CourseContext);
  if (!context) {
    throw new Error('useCourse must be used within a CourseProvider');
  }
  return context;
};

// ─── Mapping champs API → champs frontend ─────────────────────────────────────
const DIFFICULTY_LABELS = {
  beginner:     'Débutant',
  intermediate: 'Intermédiaire',
  advanced:     'Avancé',
};

function formatDuration(mins) {
  if (!mins) return 'N/A';
  if (mins >= 600) return `${Math.round(mins / 300)} semaines`;
  if (mins >= 60)  return `${Math.round(mins / 60)}h`;
  return `${mins} min`;
}

function mapCourse(c) {
  const raw = c.instructor;
  const instructor = !raw ? 'Instructeur'
    : typeof raw === 'object'
      ? `${raw.first_name || ''} ${raw.last_name || ''}`.trim() || 'Instructeur'
      : raw;

  return {
    ...c,
    level:        DIFFICULTY_LABELS[c.difficulty] || c.difficulty || 'Débutant',
    instructor,
    duration:     formatDuration(c.estimated_duration),
    rating:       c.average_rating   || 0,
    students:     c.enrollments_count || 0,
    price:        c.pricing_price    || 0,
    originalPrice: c.pricing_price   || 0,
  };
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export const CourseProvider = ({ children }) => {
  const { user } = useAuth();
  const [courses, setCourses]               = useState([]);
  const [loading, setLoading]               = useState(true);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [favorites, setFavorites]           = useState([]);

  // Charger les cours depuis l'API au montage
  const refreshCourses = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/courses', { params: { limit: 100 } });
      setCourses((data.courses || []).map(mapCourse));
    } catch (error) {
      console.error('Erreur chargement des cours:', error);
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshCourses(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Charger les cours inscrits (si connecté)
  const loadEnrolledCourses = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/analytics/my-progress');
      const list = (data.enrollments || []).map(e => ({
        ...mapCourse(e.course || {}),
        enrollmentId: e.id,
        progress:     e.progress || 0,
        enrolledAt:   e.enrolled_at,
        completedAt:  e.completed_at,
      }));
      setEnrolledCourses(list);
    } catch {
      // Non connecté ou erreur silencieuse
    }
  }, []);

  // Charger automatiquement les inscriptions à la connexion / déconnexion
  useEffect(() => {
    if (user) {
      loadEnrolledCourses();
    } else {
      setEnrolledCourses([]);
    }
  }, [user, loadEnrolledCourses]);

  const getCourseById = (id) => courses.find(c => c.id === id || c.id === parseInt(id));

  const getCourseBySlug = (slug) => courses.find(c => c.slug === slug);

  const searchCourses = (query, filters = {}) => {
    let result = [...courses];
    if (query) {
      const q = query.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.instructor.toLowerCase().includes(q)
      );
    }
    if (filters.category && filters.category !== 'Tous') {
      result = result.filter(c => c.category === filters.category);
    }
    if (filters.level) {
      result = result.filter(c => c.level === filters.level);
    }
    if (filters.maxPrice) {
      result = result.filter(c => c.price <= filters.maxPrice);
    }
    return result;
  };

  const enrollInCourse = async (courseId) => {
    try {
      const { data } = await axios.post(`/api/courses/${courseId}/enroll`);
      await loadEnrolledCourses();
      return { success: true, data };
    } catch (error) {
      if (error.response?.status === 409) {
        await loadEnrolledCourses();
        return { success: false, alreadyEnrolled: true };
      }
      return { success: false, error: error.response?.data?.error || error.message };
    }
  };

  const updateCourseProgress = async (courseId, lessonId) => {
    try {
      const { data } = await axios.post(`/api/lessons/${lessonId}/complete`);
      // Mettre à jour localement la progression
      if (data.enrollment_progress !== undefined) {
        setEnrolledCourses(prev => prev.map(c =>
          c.id === courseId ? { ...c, progress: data.enrollment_progress } : c
        ));
      }
      return { success: true, data };
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      console.error('Erreur progression:', msg);
      return { success: false, error: msg };
    }
  };

  const toggleFavorite = async (courseId) => {
    setFavorites(prev =>
      prev.includes(courseId) ? prev.filter(id => id !== courseId) : [...prev, courseId]
    );
    return { success: true };
  };

  const createCourse = async (courseData) => {
    try {
      const { data } = await axios.post('/api/courses', courseData);
      const newCourse = mapCourse(data.course || data);
      setCourses(prev => [newCourse, ...prev]);
      return { success: true, course: newCourse };
    } catch (error) {
      const msg = error.response?.data?.error || error.message;
      console.error('Erreur création cours:', msg);
      return { success: false, error: msg };
    }
  };

  const getCategories = () => ['Tous', ...new Set(courses.map(c => c.category).filter(Boolean))];

  const getLevels = () => ['Tous', ...new Set(courses.map(c => c.level).filter(Boolean))];

  const getPopularCourses = (limit = 6) =>
    [...courses].sort((a, b) => b.students - a.students).slice(0, limit);

  const getRecommendedCourses = (userId, limit = 6) => getPopularCourses(limit);

  const value = {
    courses,
    loading,
    enrolledCourses,
    favorites,
    getCourseById,
    getCourseBySlug,
    searchCourses,
    enrollInCourse,
    updateCourseProgress,
    loadEnrolledCourses,
    toggleFavorite,
    getCategories,
    getLevels,
    getPopularCourses,
    getRecommendedCourses,
    createCourse,
    refreshCourses,
  };

  return (
    <CourseContext.Provider value={value}>
      {children}
    </CourseContext.Provider>
  );
};
